<template>
  <Card class="p-6 hover:shadow-lg transition-shadow">
    <div class="flex flex-col space-y-4">
      <!-- Header with name and status -->
      <div class="flex items-start justify-between">
        <div class="flex-1 min-w-0">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white truncate">
            {{ model.name }}
          </h3>
          <div class="mt-1 flex items-center space-x-2">
            <span
              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300"
            >
              BYOM
            </span>
            <code
              class="text-xs font-mono text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded"
            >
              {{ model.slug }}
            </code>
            <span
              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
            >
              {{ model.sdk_type }}
            </span>
          </div>
        </div>
        <span
          :class="[
            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0',
            statusBadgeColor,
          ]"
        >
          {{ statusText }}
        </span>
      </div>

      <!-- Base URL Display -->
      <div v-if="model.base_url" class="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Base URL</p>
        <p class="text-sm font-mono text-gray-900 dark:text-white break-all">
          {{ model.base_url }}
        </p>
      </div>

      <!-- Connection Info -->
      <div v-if="model.connection_slug" class="flex items-center space-x-2 text-xs">
        <span class="text-gray-500 dark:text-gray-400">Imported from:</span>
        <span class="font-mono text-blue-600 dark:text-blue-400">{{ model.connection_slug }}</span>
      </div>

      <!-- Cost Tracking Information -->
      <div v-if="hasCostTracking" class="grid grid-cols-2 gap-3">
        <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Input Cost</p>
          <p class="text-sm font-semibold text-gray-900 dark:text-white">
            ${{ (model.input_cost_1m ?? 0).toFixed(4)
            }}<span class="text-xs font-normal text-gray-500 dark:text-gray-400">/1M tokens</span>
          </p>
        </div>
        <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Output Cost</p>
          <p class="text-sm font-semibold text-gray-900 dark:text-white">
            ${{ (model.output_cost_1m ?? 0).toFixed(4)
            }}<span class="text-xs font-normal text-gray-500 dark:text-gray-400">/1M tokens</span>
          </p>
        </div>
      </div>
      <div
        v-else
        class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3"
      >
        <p class="text-xs text-yellow-800 dark:text-yellow-300 flex items-center">
          <svg
            class="w-4 h-4 mr-1.5 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Cost tracking not configured
        </p>
      </div>

      <!-- Model Info -->
      <div class="flex items-center justify-between text-sm">
        <span class="text-gray-500 dark:text-gray-400">Model Type</span>
        <span class="font-medium text-gray-900 dark:text-white">Custom (BYOM)</span>
      </div>

      <!-- Actions -->
      <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
        <div class="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            class="flex-1"
            :disabled="isProcessing"
            @click="handleEdit"
          >
            <svg
              class="w-4 h-4 mr-1.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            Edit
          </Button>
          <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- icon-only delete action in a menu, not a standalone Button -->
          <Button
            variant="ghost"
            size="sm"
            class="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            :disabled="isProcessing"
            @click="handleDelete"
          >
            <svg
              class="w-4 h-4 mr-1.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            Delete
          </Button>
        </div>
      </div>
    </div>
  </Card>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { Card, Button } from '@asabdev/asab-ui';
  import type { CustomModelResponse } from '../types';

  interface Props {
    model: CustomModelResponse;
  }

  interface Emits {
    (e: 'edit', model: CustomModelResponse): void;
    (e: 'delete', model: CustomModelResponse): void;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<Emits>();

  // Local state
  const isProcessing = ref(false);

  // Computed properties
  const statusBadgeColor = computed(() => {
    return props.model.is_enabled
      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
      : 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300';
  });

  const statusText = computed(() => {
    return props.model.is_enabled ? 'Enabled' : 'Disabled';
  });

  const hasCostTracking = computed(() => {
    return (
      props.model.input_cost_1m !== null &&
      props.model.input_cost_1m !== undefined &&
      props.model.output_cost_1m !== null &&
      props.model.output_cost_1m !== undefined
    );
  });

  // Methods
  const handleEdit = () => {
    if (isProcessing.value) return;
    emit('edit', props.model);
  };

  const handleDelete = () => {
    if (isProcessing.value) return;
    emit('delete', props.model);
  };
</script>
